import React, { useEffect, useRef, useState } from "react";
import Vex from "vexflow";
import { BookOpen, ChevronDown } from "lucide-react";

const VF = Vex.Flow;
const KIT = [
  ["a/5/x2", "Crash"], ["f/5/x2", "Ride"], ["f/5/d2", "Ride bell"], ["g/5/x2", "Hi-hat"],
  ["e/5", "High tom"], ["d/5", "Mid tom"], ["c/5", "Snare"], ["c/5/x2", "Rim"], ["a/4", "Floor tom"], ["f/4", "Kick"],
];

function drawStave(host, w, notes, opts = {}) {
  const r = new VF.Renderer(host, VF.Renderer.Backends.SVG);
  r.resize(w, 140);
  const ctx = r.getContext();
  const stave = new VF.Stave(0, 20, w - 4);
  if (opts.clef) stave.addClef("percussion");
  if (opts.repeat) { stave.setBegBarType(VF.BarlineType.REPEAT_BEGIN); stave.setEndBarType(VF.BarlineType.REPEAT_END); }
  stave.setContext(ctx).draw();
  const voice = new VF.Voice({ num_beats: notes.length, beat_value: 4 }).setMode(VF.Voice.Mode.SOFT);
  voice.addTickables(notes);
  new VF.Formatter().joinVoices([voice]).formatToStave([voice], stave);
  voice.draw(ctx, stave);
  return { ctx, stave };
}

const label = (t) => new VF.Annotation(t).setFont("Arial", 10).setVerticalJustification(VF.Annotation.VerticalJustify.BOTTOM);
const note = (key, opts = {}) => new VF.StaveNote({ clef: "percussion", keys: [key], duration: opts.dur || "q", stem_direction: 1 });

const KeyStaves = ({ width }) => {
  const kitRef = useRef(null);
  const symRef = useRef(null);
  useEffect(() => {
    if (!kitRef.current || !symRef.current) return;
    kitRef.current.innerHTML = "";
    symRef.current.innerHTML = "";
    const w = Math.max(520, Math.min(width || 900, 1000));
    drawStave(kitRef.current, w, KIT.map(([k, t]) => note(k).addModifier(label(t), 0)), { clef: true });
    const ghost = note("c/5");
    ghost.addModifier(new VF.Parenthesis(VF.Modifier.Position.LEFT), 0).addModifier(new VF.Parenthesis(VF.Modifier.Position.RIGHT), 0).addModifier(label("Ghost (soft)"), 0);
    const acc = note("c/5").addModifier(new VF.Articulation("a>").setPosition(VF.Modifier.Position.ABOVE), 0).addModifier(label("Accent (loud)"), 0);
    const open = note("g/5/x2").addModifier(new VF.Annotation("o").setVerticalJustification(VF.Annotation.VerticalJustify.TOP), 0).addModifier(label("Open hi-hat"), 0);
    const r = note("c/5").addModifier(label("R = right hand"), 0);
    const l = note("c/5").addModifier(label("L = left hand"), 0);
    drawStave(symRef.current, Math.min(w, 620), [ghost, acc, open, r, l], { repeat: true });
  }, [width]);
  return (
    <>
      <div ref={kitRef} className="overflow-x-auto" data-testid="notation-key-kit" />
      <div ref={symRef} className="overflow-x-auto" data-testid="notation-key-symbols" />
    </>
  );
};

const TIPS = [
  ["Repeat signs", "Thick bars with two dots (𝄆 … 𝄇) mean play the bars between them again. “×4” = play that part 4 times in total."],
  ["1st / 2nd endings", "Brackets marked 1. and 2. over the last bars: play bar 1. every time except the last, then skip it and play bar 2. instead."],
  ["Swing", "“Swing ♫ = ♩³♪” means play pairs of 8th notes long-short, like a triplet shuffle."],
  ["Triplets", "A bracket with a 3 (or 6) means fit 3 (or 6) evenly spaced notes into the space of 2 (or 4)."],
  ["Sticking", "R / L under notes shows which hand to use. Black = BeatFlow suggestion, blue = your own sticking."],
  ["Ghost notes", "Notes in brackets ( ) are played very softly, just above the drum head — they add feel, not volume."],
];

export const NotationKey = ({ width }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="notation-key">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-tab uppercase tracking-widest text-slate-300 hover:text-[#00f0ff] transition-colors" data-testid="notation-key-toggle">
        <span className="inline-flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Notation key — how to read this chart</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid="notation-key-panel">
          <div className="rounded-lg bg-[#f7f4ec] text-[#1a1a1a] px-3 py-2">
            <KeyStaves width={width} />
          </div>
          <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {TIPS.map(([t, d]) => (
              <div key={t} className="text-xs text-slate-400"><dt className="inline font-semibold text-[#ffab00]">{t}: </dt><dd className="inline">{d}</dd></div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};
